import styled from 'styled-components';

/**
 * Lead Modal Styled Components
 * Shared styled components used across lead-related modals
 */

export const AddEmail = "Enter email address";
export const AddCompany = "Enter company name";
export const AddName = "Enter full name";
export const AddPhone = "Enter 10-digit phone number";
export const AddNotes = "Add notes here...";
export const AddFollowupOn = "Follow-up On:";

/**
 * Modal Content Container
 * Main container for modal content with consistent styling
 */
export const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 10px;
  min-width: 320px;
  max-width: 90vw;
  font-family: 'Inria Serif', serif;
`;

/**
 * Form Field Container
 * Container for individual form fields with consistent spacing
 */
export const Field = styled.div`
  margin-bottom: 16px;
  font-family: 'Inria Serif', serif;
`;

/**
 * Form Label
 * Styled label for form fields
 */
export const Label = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  font-family: 'Inria Serif', serif;
`;

/**
 * Display Value
 * Styled container for displaying non-editable values
 */
export const Value = styled.div`
  margin-bottom: 4px;
  font-family: 'Inria Serif', serif;
`;

/**
 * Editable Input Field
 * Standard input field with focus states and error handling
 */
export const EditableInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  font-family: 'Inria Serif', serif;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  &.error {
    border-color: #dc3545;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
  }
`;

/**
 * Phone Input Container
 * Container for phone input with prefix display
 */
export const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  &:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  &.error {
    border-color: #dc3545;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
  }
`;

/**
 * Phone Prefix Display
 * Styled prefix for phone number input
 */
export const PhonePrefix = styled.div`
  background: #f8f9fa;
  padding: 8px 12px;
  border-right: 1px solid #ccc;
  font-family: 'Inria Serif', serif;
  color: #6c757d;
  font-weight: 500;
`;

/**
 * Phone Input Field
 * Input field specifically for phone numbers
 */
export const PhoneInput = styled.input`
  flex: 1;
  padding: 8px;
  border: none;
  font-size: 1rem;
  font-family: 'Inria Serif', serif;
  &:focus {
    outline: none;
  }
`;

/**
 * Notes Textarea
 * Textarea for notes with consistent styling
 */
export const NotesInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  border-radius: 6px;
  border: 1px solid #ccc;
  padding: 8px;
  font-size: 1rem;
  font-family: 'Inria Serif', serif;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

/**
 * Button Row Container
 * Container for action buttons with consistent spacing
 */
export const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

/**
 * Header Row Container
 * Container for modal header with flexible layout
 */
export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

/**
 * Modal Button
 * Standard button with primary and cancel variants
 */
export const Button = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  background: #007bff;
  color: #fff;
  font-family: 'Inria Serif', serif;
  &:hover {
    background: #0056b3;
  }
  &[data-variant='cancel'] {
    background: #ccc;
    color: #222;
    &:hover {
      background: #999;
    }
  }
`;

/**
 * Date Time Row Container
 * Container for date and time inputs
 */
export const DateTimeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  font-family: 'Inria Serif', serif;
`;

/**
 * Error Message
 * Styled error message display
 */
export const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
  font-family: 'Inria Serif', serif;
`;

/**
 * Modal Styles Configuration
 * Standard modal styles for consistent appearance
 */
export const modalStyles = {
  overlay: {
    zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.2)'
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '400px',
    width: '90vw',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: '24px',
    fontFamily: 'Inria Serif, serif',
  }
};

/**
 * Date/Time Input Styles
 * Inline styles for date and time inputs
 */
export const dateTimeInputStyles = {
  fontFamily: 'Inria Serif',
  fontSize: '1em',
  padding: '2px 6px',
  borderRadius: 4,
  border: '1px solid #bbb'
};
